// @ts-nocheck

import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, Calendar, Users, MapPin, Upload, FileText } from "lucide-react";
import { EventFormData } from "../CreateEventForm";
import { supabase } from "@/lib/supabase";
import FeaturedImageUpload from "./FeaturedImageUpload";
import CoverPhotoUpload from "./CoverPhotoUpload";
import { useSportsByGender } from "@/hooks/useSportsByGender";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "sonner";

interface BasicInfoTabProps {
  formData: EventFormData;
  updateFormData: (data: Partial<EventFormData>) => void;
}

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({ formData, updateFormData }) => {
  const [eventTypes, setEventTypes] = useState<any[]>([]);
  const [eventCategories, setEventCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Use the hook to get filtered sports based on gender
  const { sports, isLoading: sportsLoading } = useSportsByGender(formData.gender);

  // Quill editor configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  }), []);

  const quillFormats = [
    'bold', 'italic', 'underline', 'list', 'bullet', 'link'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [eventTypesResult, eventCategoriesResult] = await Promise.all([
        supabase.from('event_types').select('*').order('name'),
        supabase.from('event_categories').select('*').order('name')
      ]);

      if (eventTypesResult.data) setEventTypes(eventTypesResult.data);
      if (eventCategoriesResult.data) setEventCategories(eventCategoriesResult.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    const currentCategories = formData.event_category_ids || [];
    const isSelected = currentCategories.includes(categoryId);
    
    if (isSelected) {
      updateFormData({
        event_category_ids: currentCategories.filter(id => id !== categoryId)
      });
    } else {
      updateFormData({
        event_category_ids: [...currentCategories, categoryId]
      });
    }
  };

  const removeCategoryById = (categoryId: string) => {
    const currentCategories = formData.event_category_ids || [];
    updateFormData({
      event_category_ids: currentCategories.filter(id => id !== categoryId)
    });
  };

  const getSelectedCategoryNames = () => {
    const selectedIds = formData.event_category_ids || [];
    return eventCategories.filter(cat => selectedIds.includes(cat.id));
  };

  const handleImageChange = (imageUrl: string | null) => {
    updateFormData({ thumbnail_url: imageUrl });
  };

  const handleCoverPhotoChange = (imageUrl: string | null) => {
    updateFormData({ cover_photo_url: imageUrl });
  };

  const handleDescriptionChange = (content: string) => {
    updateFormData({ description: content });
  };

  const handlePacketUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    try {
      const fileExt = 'pdf';
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `events/${formData.id || 'temp'}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('purchasable-packets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('purchasable-packets')
        .getPublicUrl(filePath);

      updateFormData({ purchasable_packet_url: urlData.publicUrl });
      toast.success('Packet uploaded successfully');
    } catch (error) {
      console.error('Error uploading packet:', error);
      toast.error('Failed to upload packet');
    }
  };

  const handleRemovePacket = async () => {
    if (!formData.purchasable_packet_url) return;

    try {
      // Extract file path from URL
      const url = new URL(formData.purchasable_packet_url);
      const filePath = url.pathname.replace('/storage/v1/object/public/purchasable-packets/', '');

      await supabase.storage
        .from('purchasable-packets')
        .remove([filePath]);

      updateFormData({ purchasable_packet_url: '' });
      toast.success('Packet removed successfully');
    } catch (error) {
      console.error('Error removing packet:', error);
      toast.error('Failed to remove packet');
    }
  };

  // Filter out options with empty or invalid IDs
  const validSports = sports.filter(sport => sport.id && sport.id.trim() !== '');
  const validEventTypes = eventTypes.filter(type => type.id && type.id.trim() !== '');
  const validEventCategories = eventCategories.filter(cat => cat.id && cat.id.trim() !== '');

  return (
    <div className="space-y-6">
      {/* Event Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Event Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Event Title *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => updateFormData({ name: e.target.value })}
                placeholder="Enter event title"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="gender">Gender</Label>
              <Select value={formData.gender || ""} onValueChange={(value) => updateFormData({ gender: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Men">Men</SelectItem>
                  <SelectItem value="Women">Women</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Event Description</Label>
            <div className="mt-1">
              <ReactQuill
                theme="snow"
                value={formData.description || ""}
                onChange={handleDescriptionChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Describe your event..."
                style={{ height: '200px', marginBottom: '50px' }}
              />
            </div>
          </div>

          {/* Featured Image Upload */}
          <FeaturedImageUpload
            currentImageUrl={formData.thumbnail_url}
            onImageChange={handleImageChange}
          />

          <CoverPhotoUpload
            currentImageUrl={formData.cover_photo_url}
            onImageChange={handleCoverPhotoChange}
          />
        </CardContent>
      </Card>

      {/* Event Classification Card */}
      <Card>
        <CardHeader>
          <CardTitle>Event Classification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sport_id">Sport *</Label>
              <Select value={formData.sport_id || ""} onValueChange={(value) => updateFormData({ sport_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sport" />
                </SelectTrigger>
                <SelectContent>
                  {validSports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id}>
                      {sport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="event_type_id">Event Type *</Label>
              <Select value={formData.event_type_id || ""} onValueChange={(value) => updateFormData({ event_type_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent>
                  {validEventTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Event Categories */}
          <div>
            <Label>Event Categories (Optional)</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Select categories that best describe your event
            </p>
            
            {/* Selected Categories */}
            {getSelectedCategoryNames().length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {getSelectedCategoryNames().map((category) => (
                  <Badge key={category.id} variant="secondary" className="flex items-center gap-1">
                    {category.name}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 ml-1"
                      onClick={() => removeCategoryById(category.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Available Categories */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {validEventCategories.map((category) => {
                const isSelected = (formData.event_category_ids || []).includes(category.id);
                return (
                  <Button
                    key={category.id}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleCategoryToggle(category.id)}
                    className="justify-start text-left"
                  >
                    {category.name}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Dates Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Event Dates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => updateFormData({ start_date: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => updateFormData({ end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="registration_open_date">Registration Opens</Label>
              <Input
                id="registration_open_date"
                type="date"
                value={formData.registration_open_date}
                onChange={(e) => updateFormData({ registration_open_date: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="registration_close_date">Registration Closes</Label>
              <Input
                id="registration_close_date"
                type="date"
                value={formData.registration_close_date}
                onChange={(e) => updateFormData({ registration_close_date: e.target.value })}
              />
            </div>
          </div>

        </CardContent>
      </Card>

      {/* More Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>More Info</CardTitle>
          <p className="text-sm text-muted-foreground">
            Additional information to display in a blue box under Helpful Information on the event page
          </p>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="more_info">More Info Text</Label>
            <ReactQuill
              value={formData.more_info}
              onChange={(value) => updateFormData({ more_info: value })}
              modules={quillModules}
              theme="snow"
              placeholder="Enter additional information..."
            />
            <p className="text-xs text-muted-foreground mt-2">
              This will appear in a blue box under the Helpful Information section on the event page
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BasicInfoTab;